<script setup lang="ts">
import {useAppDataStore} from '@/stores/app-data'
import {useCharacterDataStore} from "@/stores/character-data";
import {onMounted, ref} from "vue";
import type {WorldInterface} from "@/types/worlds";
import WorldFinder from "@/components/atoms/WorldFinder.vue";
import {useUxData} from "@/stores/ux-data";
import {usePredicateStore} from "@/stores/predicate-store";
import type {SystemInterface} from "@/types/systems";
import {useAuthDataStore} from "@/stores/auth-data.ts";
import {handleError} from "@/appdata/globals.ts";
import {useModal} from "@/composables/useModal";
import router from '@/router';

const props = defineProps<{
  system?: SystemInterface | null
}>()
const auth = useAuthDataStore();
const store = useAppDataStore();
const swade = useCharacterDataStore();
const predicateStore = usePredicateStore();
const ux = useUxData();

const nameInvalid = ref(false);

// WorldFinder wraps an uncontrolled Kendo AutoComplete that keeps the last selection in its own
// internal state. The modal stays mounted between opens, so bumping this key on each show forces a
// fresh finder — reverting its displayed value to empty alongside the placeholder reset below.
const finderKey = ref(0);

const onClickCreate = async () => {
  console.info("CharacterCreator | OptionsModal | onClickCreate");

  // Block creation of a nameless character (and surface the required-name notification) before
  // adding it to the list. Reuses the store's authoritative guard so the message stays in one place.
  if (!store._requireCharacterName(swade.placeholder.character)) {
    nameInvalid.value = true;
    return;
  }
  nameInvalid.value = false;

  const world = swade.placeholder.character?.world ?? null;
  const system = swade.placeholder.character?.system ?? props.system ?? null;

  console.debug(world);
  const newCharacter = {
    ...swade.newCharacter(),
    ...{
      uuid: null,
      name: swade.placeholder.character?.name,
      system: system,
      world: world,
      updated: (new Date()).toUTCString(),
    }
  };

  // Not into a character list: those are fetched, and the next fetch would drop it. The store
  // keeps unsaved characters in a slot of their own, which every character list shows on top.
  store.unsavedCharacters.unshift(newCharacter)

  hide();

  // A character is created to be edited. The editor at /characters/new opens the first unsaved
  // character — the one just created.
  router.push({ name: 'character', params: { character: 'new' } });
}

const onWorldChange = (event: any) => {
  console.info("CharacterCreator | NewCharacterOptions | onWorldChange")
  console.debug("CharacterCreator | NewCharacterOptions | onWorldChange | ", event)
  if (!swade.placeholder.character) return;

  if (event.item) {
    swade.placeholder.character.world = {
      uuid: event.item.uuid,
      name: event.item.name,
    }

    store.retrieveWorld(event.item)
        .catch((error) => { handleError(error) })
  }
}

const onWorldCleared = () => {
  console.info("CharacterCreator | NewCharacterOptions | onWorldCleared")
  if (!swade.placeholder.character) return;
  swade.placeholder.character.world = null;
}

const resetPlaceholder = () => {
  swade.placeholder.character = swade.newCharacter();
  if (swade.placeholder.character) {
    swade.placeholder.character.world = null;
  }
}

const modalRef = ref<HTMLElement | null>(null);
const { show, hide } = useModal(modalRef, {
  onShow: () => { resetPlaceholder(); nameInvalid.value = false; finderKey.value++; },
});

onMounted(() => {
  predicateStore.ownedOnly = false;
  resetPlaceholder();
})

// A host opens this modal through show(). Some openers still use Bootstrap's data API by design
// (CharacterList and CharacterCreatorView's data-bs-toggle="modal" buttons); useModal reports
// those opens the same way.
defineExpose({ show });
</script>

<template>
  <div id="create-character-options" ref="modalRef" class="modal fade" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header header character-options">{{ ux.labels.tools.modalCharacterNew.header }}</div>
        <div class="modal-body">
          <div class="form-group mb-3">
            <label class="required">{{ ux.labels.data.system }}</label>
            <div class="system-name">{{ props.system?.name ?? store.system?.name }}</div>
          </div>
          <div v-if="auth.canSelectWorld" class="form-group mb-3">
            <label>{{ ux.labels.data.world }}</label>
            <WorldFinder
              :key="finderKey"
              :placeholder="ux.labels.filters.world.placeholder"
              :include-all="false"
              @world-selected="onWorldChange"
              @world-cleared="onWorldCleared"
            />
          </div>

          <div class="form-group mb-3">
            <label for="new-character-name" class="required">{{ ux.labels.tools.modalCharacterNew.name.label }}</label>
            <input id="new-character-name" type="text"
                   :class="['form-control', 'form-control-lg', { 'is-invalid': nameInvalid }]"
                   :placeholder="ux.labels.tools.modalCharacterNew.name.placeholder"
                   @input="nameInvalid = false"
                   v-model="swade.placeholder.character.name"/>
            <div v-if="nameInvalid" class="invalid-feedback d-block">
              {{ ux.labels?.validation?.character?.nameRequired ?? 'Please enter a name for your character before saving.' }}
            </div>
          </div>
        </div>
        <div class="modal-footer d-flex flex-fill justify-content-between">
          <button type="button" class="btn btn-link text-muted rounded-pill text-uppercase"
                  data-bs-dismiss="modal"
                  @click="swade.cancelNewCharacterContext()"
          >
            {{ ux.labels.actions.cancel }}
          </button>

          <button type="button" class="btn btn-link text-success rounded-pill text-uppercase"
                  @click="onClickCreate"
          >
            {{ ux.labels.actions.create }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
